import { Link } from 'react-router-dom';
import { Tent, ArrowLeft, Mail } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Tent className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="font-heading font-bold text-3xl">Terms of Use</h1>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 md:p-8 space-y-8">
            <p className="text-muted-foreground">
              Last updated: March 2026
            </p>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">1. About Campia</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia is a directory platform that connects parents, schools, and organisations with camping venues 
                and camp facilitators in South Africa. Campia is a <strong>directory service only</strong> — we do 
                not provide endorsements, recommendations, or guarantees about listed camps or facilitators.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">2. Verification Status</h2>
              <p className="text-muted-foreground leading-relaxed">
                Profiles on Campia may display a "Verified" status. This verification means:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>The facilitator or camp has submitted verification documents (such as ID, police clearance, or qualifications)</li>
                <li>These documents have been reviewed by a Campia administrator</li>
                <li>The information provided appears authentic at the time of review</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <strong>Important:</strong> Verification does NOT guarantee character, conduct, ongoing compliance, 
                  or suitability for any particular purpose. Users must conduct their own due diligence before engaging 
                  with any listed facilitator or camp.
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">3. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia is not liable for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Any interactions, agreements, or disputes between users and listed facilitators or camps</li>
                <li>The accuracy, completeness, or currency of information provided by users</li>
                <li>Any harm, injury, or loss arising from the use of services provided by listed parties</li>
                <li>Any technical issues, downtime, or data loss on the platform</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Users engage with listed facilitators and camps at their own risk and are encouraged to verify 
                credentials independently before making any commitments.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">4. User Conduct</h2>
              <p className="text-muted-foreground leading-relaxed">
                By using Campia, you agree to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Provide accurate and truthful information in your profile and reviews</li>
                <li>Not submit false, misleading, or defamatory reviews</li>
                <li>Not create fake profiles or impersonate others</li>
                <li>Not use the platform for any unlawful purpose</li>
                <li>Respect the intellectual property rights of others</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">5. Content Moderation</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia reserves the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Remove any profile, review, or content that violates these terms</li>
                <li>Suspend or terminate accounts that breach these terms</li>
                <li>Modify or update verification status based on new information</li>
                <li>Refuse service to any user at our discretion</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                All reviews are subject to moderation before publication. We aim to ensure reviews are genuine, 
                fair, and helpful to the community.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">6. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Campia name, logo, and platform design are the property of Campia. Users retain ownership 
                of content they submit but grant Campia a license to display, reproduce, and distribute this 
                content within the platform.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">7. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia may update these terms from time to time. We will notify users of significant changes 
                via email or platform notification. Continued use of Campia after changes constitutes acceptance 
                of the updated terms.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">8. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these terms, please contact us:
              </p>
              <div className="flex items-center gap-2 text-primary">
                <Mail className="w-4 h-4" />
                <a href="mailto:legal@campia.co.za" className="hover:underline">legal@campia.co.za</a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
